/**
 * Centralized Coupon Price Calculator
 * Handles price calculations for all coupon types
 */

import {
  isBXGYCoupon,
  getBuyItemCount,
  getGetItemCount,
} from './types';
import calculateBXGYQuantity from './calculateBXGYQuantity';

/**
 * Calculate prices for Free Item coupon (Type 0)
 * Only 1 item is free (main item free, only options paid)
 * Additional items are paid at full price
 *
 * @param {Object} params
 * @param {Object} params.item - Item data (from basket or product)
 * @param {Object} params.coupon - Applied coupon
 * @param {number} params.qty - Quantity
 * @param {number} params.itemPrice - Original item price
 * @param {number} params.optionsPricePerItem - Options price per item
 * @returns {Object} { originalTotal, displayTotal, isFreeItem, freeQuantity, paidQuantity }
 */
export const calculateFreeItemPrices = ({
  itemPrice = 0,
  optionsPricePerItem = 0,
  qty = 1,
}) => {
  // Only 1 item is free, rest are paid
  const freeQuantity = 1;
  const paidQuantity = Math.max(0, qty - 1);

  // Free item: main item is free, only options are paid for the free item
  const freeItemTotal = 0 + (optionsPricePerItem * freeQuantity); // Main item free, options paid

  // Paid items: full price (itemPrice + optionsPricePerItem)
  const paidItemsTotal = (itemPrice + optionsPricePerItem) * paidQuantity;

  const displayTotal = freeItemTotal + paidItemsTotal;

  // Original total (for strikethrough) - if all items were paid
  const originalTotal = (itemPrice + optionsPricePerItem) * qty;

  return {
    originalTotal,
    displayTotal,
    savedAmount: originalTotal - displayTotal,
    isFreeItem: true,
    freeQuantity,
    paidQuantity,
  };
};

/**
 * Calculate prices for BXGY coupon (Type 2)
 * Main item: paid for paidQuantity only
 * Options: paid for totalQuantity (all items)
 *
 * @param {Object} params
 * @param {Object} params.coupon - Applied coupon
 * @param {number} params.qty - Total quantity (user selected)
 * @param {number} params.itemPrice - Item price
 * @param {number} params.optionsPricePerItem - Options price per item
 * @returns {Object} { originalTotal, displayTotal, paidQuantity, freeQuantity, totalQuantity }
 */
export const calculateBXGYPrices = ({
  coupon,
  qty = 1,
  itemPrice = 0,
  optionsPricePerItem = 0,
}) => {
  if (!coupon || !isBXGYCoupon(coupon)) {
    return {
      originalTotal: (itemPrice + optionsPricePerItem) * qty,
      displayTotal: (itemPrice + optionsPricePerItem) * qty,
      paidQuantity: qty,
      freeQuantity: 0,
      totalQuantity: qty,
    };
  }

  const bxgyResult = calculateBXGYQuantity(coupon, qty);
  const { paidQuantity, freeQuantity, totalQuantity } = bxgyResult;

  // BXGY calculation:
  // - Main item: paidQty × itemPrice (free items don't pay for main item)
  // - Options: totalQty × optionsPrice (options apply to ALL items including free)
  const mainItemTotal = itemPrice * paidQuantity;
  const optionsTotal = optionsPricePerItem * totalQuantity;
  const displayTotal = mainItemTotal + optionsTotal;

  // Original total (for strikethrough) - if all items were paid
  const originalTotal = (itemPrice + optionsPricePerItem) * totalQuantity;

  return {
    originalTotal,
    displayTotal,
    savedAmount: originalTotal - displayTotal,
    paidQuantity,
    freeQuantity,
    totalQuantity,
  };
};

/**
 * Calculate prices based on coupon type
 * Unified interface for all coupon types
 *
 * @param {Object} params
 * @param {Object} params.coupon - Applied coupon (or null)
 * @param {number} params.qty - Quantity
 * @param {number} params.itemPrice - Item price
 * @param {number} params.optionsPricePerItem - Options price per item
 * @param {boolean} params.isFreeItem - Is this a free item
 * @param {boolean} params.isBXGYItem - Is this a BXGY item
 * @returns {Object} Price calculation result
 */
export const calculateCouponPrices = ({
  coupon,
  qty,
  itemPrice,
  optionsPricePerItem,
  isFreeItem = false,
  isBXGYItem = false,
}) => {
  // Free Item (Type 0)
  if (isFreeItem) {
    return calculateFreeItemPrices({
      itemPrice,
      optionsPricePerItem,
      qty,
    });
  }

  // BXGY (Type 2)
  if (isBXGYItem && coupon) {
    return calculateBXGYPrices({
      coupon,
      qty,
      itemPrice,
      optionsPricePerItem,
    });
  }

  // No coupon or Type 1 (discount handled by backend)
  const total = (itemPrice + optionsPricePerItem) * qty;
  return {
    originalTotal: total,
    displayTotal: total,
    savedAmount: 0,
    paidQuantity: qty,
    freeQuantity: 0,
    totalQuantity: qty,
  };
};

/**
 * Get BXGY display info for UI
 * @param {Object} coupon - Applied coupon
 * @returns {Object} { buyCount, getCount, label }
 */
export const getBXGYDisplayInfo = (coupon) => {
  if (!coupon || !isBXGYCoupon(coupon)) return null;

  const buyCount = getBuyItemCount(coupon);
  const getCount = getGetItemCount(coupon);

  return {
    buyCount,
    getCount,
    label: `Buy ${buyCount}, Get ${getCount} Free!`,
  };
};

/**
 * Calculate basket item prices with coupon
 * Used by BasketProduct components
 *
 * @param {Object} params
 * @param {Object} params.basketItem - Basket item
 * @param {Object} params.appliedCoupon - Applied coupon
 * @param {Array} params.basketItems - All basket items (for minprice check)
 * @returns {Object|null} Price calculation result or null
 */
export const calculateBasketItemCouponPrices = ({
  basketItem,
  appliedCoupon,
  basketItems = [],
}) => {
  if (!basketItem) return null;

  const { qty, options, item, product, bxgyInfo, isFreeItemFromCoupon } = basketItem;

  // Check if this is a free item
  // Must have isFreeItemFromCoupon flag to distinguish from manually added items
  const isFreeItem =
    appliedCoupon?.type === 0 &&
    appliedCoupon?.freeitemid === item?.id &&
    isFreeItemFromCoupon === true;

  // Check if this is a BXGY item
  const isBXGYItem = appliedCoupon?.type === 2 && !!bxgyInfo;

  // If neither free item nor BXGY, return null
  if (!isFreeItem && !isBXGYItem) return null;

  // Find original item price from product.items
  const originalItem = product?.items?.find((p) => p.id === item?.id);
  const itemPrice = originalItem?.price || item?.price || 0;

  // Calculate options price per item (unit price x unit qty; legacy
  // basket rows have no qty and count once)
  const optionsPricePerItem = (options || []).reduce(
    (sum, opt) => sum + (opt.price || 0) * (opt.qty ?? 1),
    0
  );

  // For Type 0 (Free Item), check if minprice is met
  if (isFreeItem) {
    // Get items that are NOT from a coupon (regular paid items)
    // Always exclude the free item itself from regularItems calculation
    // Free item's free part (1 item) is NOT included, but paid part (qty > 1) IS included
    const regularItems = basketItems.filter(
      (bItem) =>
        bItem.basketId !== basketItem.basketId && // Always exclude current free item
        !bItem.isFreeItemFromCoupon && // Exclude items added via coupon
        !bItem.bxgyInfo
    );

    // Calculate "clean" subtotal (without free items and BXGY items)
    let cleanSubtotal = regularItems.reduce((sum, bItem) => {
      return sum + (bItem.total || 0);
    }, 0);

    // Add paid part of free item (if qty > 1)
    // Only 1 free item is free, rest are paid
    // Use current item's updated price data
    if (qty > 1) {
      const paidQuantity = qty - 1;
      const paidPartTotal = (itemPrice + optionsPricePerItem) * paidQuantity;
      cleanSubtotal += paidPartTotal;
    }

    // If minprice is met, apply free item pricing (main item free, options paid)
    // If minprice is NOT met, show full price (no discount)
    const minpriceMet =
      !appliedCoupon?.minprice ||
      cleanSubtotal >= appliedCoupon.minprice;

    if (minpriceMet) {
      // Main item is free, only options are paid
      return {
        ...calculateFreeItemPrices({ itemPrice, optionsPricePerItem, qty }),
        type: 'freeItem',
      };
    } else {
      // Minprice not met, show full price (no discount)
      const total = (itemPrice + optionsPricePerItem) * qty;
      return {
        originalTotal: total,
        displayTotal: total,
        savedAmount: 0,
        type: 'freeItem',
        minpriceNotMet: true,
      };
    }
  }

  if (isBXGYItem) {
    // Check if BXGY can be applied (need at least BuyItemCount + 1)
    const bxgyResult = calculateBXGYQuantity(appliedCoupon, qty);
    
    // If canApply is false, return null (no discount, show normal price)
    if (!bxgyResult.canApply) {
      return null;
    }
    
    return {
      ...calculateBXGYPrices({
        coupon: appliedCoupon,
        qty,
        itemPrice,
        optionsPricePerItem,
      }),
      type: 'bxgy',
    };
  }

  return null;
};
